/**
 * Channel chip for Marketing Automation campaign rows.
 *
 * Triveny-4: replaces the legacy emoji-on-rainbow-tint badges (📧 📱 💬 📞 🔀)
 * with Lucide icons on a neutral `--v2-paper-2` chip (or `--v2-accent-soft`
 * for multi-channel), per design_handoff_platform_redesign/designs/marketing.jsx.
 *
 * Branches on config.isTriveny:
 *   - isTriveny  → mono UPPERCASE chip with Lucide icon
 *   - otherwise → legacy emoji-on-color badge (preserves WeaveIn appearance)
 *
 * Why a helper instead of editing every render site:
 *   The page renders this chip pattern in 5+ places. Centralizing it lets
 *   the design migrate cleanly and lets WeaveIn keep its current look with
 *   no risk of typos in 5 separate locations.
 */

import React from 'react';
import {
  Mail,
  MessageSquare,
  MessageCircle,
  Phone,
  Globe,
  Shuffle,
  LucideIcon,
} from 'lucide-react';
import { config } from '@/src/config';

export type CampaignChannel = 'email' | 'sms' | 'whatsapp' | 'voice' | 'social' | 'multi-channel';

/** Legacy emoji + Tailwind color (unchanged WeaveIn appearance). */
const LEGACY: Record<CampaignChannel, { emoji: string; label: string; color: string }> = {
  email: { emoji: '📧', label: 'Email', color: 'bg-blue-100 text-blue-800' },
  sms: { emoji: '📱', label: 'SMS', color: 'bg-green-100 text-green-800' },
  whatsapp: { emoji: '💬', label: 'WhatsApp', color: 'bg-emerald-100 text-emerald-800' },
  voice: { emoji: '📞', label: 'Voice AI', color: 'bg-purple-100 text-purple-800' },
  social: { emoji: '🌐', label: 'Social', color: 'bg-pink-100 text-pink-800' },
  'multi-channel': { emoji: '🔀', label: 'Multi-Channel', color: 'bg-indigo-100 text-indigo-800' },
};

/** Triveny: Lucide icon + neutral mono chip (multi-channel uses accent-soft). */
const TRIVENY: Record<CampaignChannel, { icon: LucideIcon; label: string; accent: boolean }> = {
  email: { icon: Mail, label: 'EMAIL', accent: false },
  sms: { icon: MessageSquare, label: 'SMS', accent: false },
  whatsapp: { icon: MessageCircle, label: 'WHATSAPP', accent: false },
  voice: { icon: Phone, label: 'VOICE AI', accent: false },
  social: { icon: Globe, label: 'SOCIAL', accent: false },
  'multi-channel': { icon: Shuffle, label: 'MULTI', accent: true },
};

const trivenyChipStyle = (accent: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 20,
  padding: '0 8px',
  borderRadius: 999,
  fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.03em',
  background: accent ? 'var(--v2-accent-soft)' : 'var(--v2-paper-2)',
  color: accent ? 'var(--v2-accent-ink)' : 'var(--v2-ink-700)',
  whiteSpace: 'nowrap',
});

export interface CampaignChannelChipProps {
  type: CampaignChannel;
  /** Whether to render in Triveny style. Defaults to config.isTriveny so most
   *  call sites can omit it. Override when rendering inside a specific tenant
   *  context (rare). */
  isTriveny?: boolean;
}

export default function CampaignChannelChip({ type, isTriveny }: CampaignChannelChipProps) {
  const triveny = isTriveny ?? config.isTriveny;

  if (triveny) {
    const variant = TRIVENY[type] ?? TRIVENY.email;
    const Icon = variant.icon;
    return (
      <span style={trivenyChipStyle(variant.accent)}>
        <Icon size={11} />
        {variant.label}
      </span>
    );
  }

  const legacy = LEGACY[type] ?? LEGACY.email;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${legacy.color}`}
    >
      <span>{legacy.emoji}</span>
      {legacy.label}
    </span>
  );
}

/**
 * Convenience: returns just the icon element (Lucide for Triveny, emoji string
 * fallback otherwise). Used in places where the chip wrapper is provided by
 * surrounding markup (e.g. the campaign-name column in the table where the
 * icon precedes the name without its own badge).
 */
export function CampaignChannelIcon({ type, isTriveny }: CampaignChannelChipProps): React.ReactElement {
  const triveny = isTriveny ?? config.isTriveny;
  if (triveny) {
    const variant = TRIVENY[type] ?? TRIVENY.email;
    const Icon = variant.icon;
    return <Icon size={16} style={{ color: 'var(--v2-ink-500)' }} />;
  }
  const legacy = LEGACY[type] ?? LEGACY.email;
  return <span className="text-xl">{legacy.emoji}</span>;
}
